import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStates } from '../../../../hooks/useStates';
import { buildDashboardGamesRows, filterDashboardGamesRows } from '../mappers/dashboardGamesMappers';

export const useDashboardGamesPageLogic = () => {
  const navigate = useNavigate();
  const {
    groupedStates,
    loading: statesLoading,
    error: statesError,
    savingStateCode,
    updateLotterySoftwareProvider,
  } = useStates();

  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState<File | null>(null);
  const [showAllRegions, setShowAllRegions] = useState(false);

  const statesTableRows = useMemo(() => buildDashboardGamesRows(groupedStates), [groupedStates]);
  const filteredStatesTableRows = useMemo(
    () => filterDashboardGamesRows(statesTableRows, stateSearchTerm, showAllRegions),
    [showAllRegions, stateSearchTerm, statesTableRows],
  );

  const handleStateChange = useCallback((stateId: string) => {
    if (!stateId) {
      return;
    }

    navigate(`/dashboard/games/${stateId}`);
  }, [navigate]);

  const handleSearchChange = useCallback((value: string) => {
    setStateSearchTerm(value);
  }, []);

  const handleCsvDialogOpen = useCallback(() => {
    setCsvDialogOpen(true);
    setSelectedCsvFile(null);
  }, []);

  const handleCsvDialogClose = useCallback(() => {
    setCsvDialogOpen(false);
    setSelectedCsvFile(null);
  }, []);

  const handleCsvFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedCsvFile(file);
  }, []);

  const handleRemoveCsvFile = useCallback(() => {
    setSelectedCsvFile(null);
  }, []);

  const handleShowAllRegionsChange = useCallback((checked: boolean) => {
    setShowAllRegions(checked);
  }, []);

  const handleCsvUpload = useCallback(() => {
    if (!selectedCsvFile) {
      return;
    }

    handleCsvDialogClose();
  }, [handleCsvDialogClose, selectedCsvFile]);

  const handleProviderSave = useCallback(async (stateCode: string, provider: string) => {
    await updateLotterySoftwareProvider(stateCode, provider);
  }, [updateLotterySoftwareProvider]);

  return {
    stateSearchTerm,
    csvDialogOpen,
    selectedCsvFile,
    showAllRegions,
    statesLoading,
    statesError,
    savingStateCode,
    filteredStatesTableRows,
    handleStateChange,
    handleSearchChange,
    handleCsvDialogOpen,
    handleCsvDialogClose,
    handleCsvFileChange,
    handleRemoveCsvFile,
    handleShowAllRegionsChange,
    handleCsvUpload,
    handleProviderSave,
  };
};
